/**
 * /auth/callback - receives access_token + refresh_token in URL fragment from
 * MD-22 Worker after Google OAuth success.
 *
 * Flow:
 *   1. Worker /api/v1/auth/google/callback handler issues 302 to:
 *      https://asklaw.pages.dev/auth/callback#access_token=...&refresh_token=...&expires_in=...
 *   2. This component reads fragment (NOT search params - avoids Referer leak)
 *   3. Stores access_token in localStorage
 *   4. Stores refresh_token in HttpOnly cookie via fetch to backend (Phase 2)
 *      For Day-1: store both in localStorage (acceptable for SPA + JWT short-TTL)
 *   5. Reads `asklaw_post_login` cookie set before OAuth start, navigates there
 *   6. Cleans up fragment from URL via history.replaceState
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyAuthChanged } from '../use-auth';

function parseFragment(): Record<string, string> {
  const hash = window.location.hash.slice(1);
  const out: Record<string, string> = {};
  for (const part of hash.split('&')) {
    const [k, v] = part.split('=');
    if (k && v) out[k] = decodeURIComponent(v);
  }
  return out;
}

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [k, v] = part.trim().split('=');
    if (k === name) return v ? decodeURIComponent(v) : null;
  }
  return null;
}

export function AuthCallbackRoute() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = parseFragment();
    const accessToken = params.access_token;
    const refreshToken = params.refresh_token;

    if (!accessToken || !refreshToken) {
      setError('Không nhận được token từ Google. Vui lòng thử lại.');
      return;
    }

    try {
      window.localStorage.setItem('asklaw_access_token', accessToken);
      window.localStorage.setItem('asklaw_refresh_token', refreshToken);
      window.localStorage.setItem(
        'asklaw_token_issued_at',
        String(Date.now())
      );
      notifyAuthChanged();
    } catch {
      setError('Trình duyệt chặn lưu trữ. Vui lòng cho phép cookie/localStorage.');
      return;
    }

    // Read post-login destination
    const dest = readCookie('asklaw_post_login') ?? '/chat';
    document.cookie = 'asklaw_post_login=; Path=/; Max-Age=0; SameSite=Lax';

    // Clean fragment
    window.history.replaceState({}, '', window.location.pathname);

    navigate(dest, { replace: true });
  }, [navigate]);

  return (
    <main style={{ maxWidth: 460, margin: '64px auto', padding: '0 16px', textAlign: 'center' }}>
      {error ? (
        <>
          <h1>Đăng nhập thất bại</h1>
          <p style={{ color: 'var(--color-muted)' }}>{error}</p>
          <a className="cta-primary" href="/auth/login" style={{ marginTop: 16 }}>
            Thử lại
          </a>
        </>
      ) : (
        <>
          <h1>Đang đăng nhập...</h1>
          <p style={{ color: 'var(--color-muted)' }}>Vui lòng chờ trong giây lát.</p>
        </>
      )}
    </main>
  );
}
